import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Theme context — manages the customer SPA's light/dark theme.
 * Two-value toggle (operator-requested simplification): just light and
 * dark, no `system`. Mirrors the visible legacy `wf-audit/.../themeSwitcher.tsx`
 * Sun/Moon dropdown but without the system option that operators
 * found confusing. Disabled the third-party `next-themes` dep —
 * everything lives in this ~150 LOC context.
 *
 * Behaviour:
 *   - The `dark` class is applied to `document.documentElement` when
 *     theme is dark, removed when light. Tailwind's `dark:` modifier
 *     reads this class. See `src/index.css` `:root` vs `.dark` blocks.
 *   - Default theme is `dark` (the WorkFocus brand is dark-navy).
 *   - localStorage-persisted under `wf-coe-theme`.
 *   - Cross-tab sync via `storage` events.
 *   - Brief no-transition style injection during the swap so colours
 *     don't fade for ~300ms after the toggle (jarring on dashboard
 *     surfaces with many simultaneous transitions). Matches what
 *     `next-themes` calls `disableTransitionOnChange`.
 *
 * Pre-paint flash mitigation: `index.html` runs an inline script that
 * reads `localStorage['wf-coe-theme']` and slaps the `dark` class on
 * `<html>` before React boots. Without that, the page paints with the
 * default theme for one frame before this provider swaps it.
 */

export type Theme = 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'wf-coe-theme';
const DARK_CLASS = 'dark';

interface ThemeContextValue {
  /** Current theme: 'light' | 'dark'. */
  theme: Theme;
  /** Same as `theme` — kept for backwards compatibility with components
   *  that branched on a separate "resolved" value back when `system`
   *  was a supported third option. */
  resolvedTheme: ResolvedTheme;
  /** Persist + apply a new theme selection. */
  setTheme: (next: Theme) => void;
  /** Convenience — flip between dark and light. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
  /** Override default — useful in tests. Defaults to `'dark'`. */
  defaultTheme?: Theme;
}

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark';
}

function readStoredTheme(): Theme | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isTheme(raw) ? raw : null;
  } catch {
    // localStorage unavailable (private-mode quotas, blocked third-
    // party cookies, etc.). Fall through to default-theme behaviour.
    return null;
  }
}

function persistTheme(theme: Theme): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* swallow — same reasons as readStoredTheme. */
  }
}

/**
 * No `system` mode anymore (operator-requested), so `resolve` is a
 * pass-through. Kept as a function so the call sites' shape doesn't
 * change.
 */
function resolve(theme: Theme): ResolvedTheme {
  return theme;
}

/**
 * Apply (or remove) the `dark` class on `<html>` to match the resolved
 * theme. Tailwind's `dark:` variant reads this class; the CSS HSL
 * tokens in `index.css` cascade from `.dark { … }` accordingly.
 *
 * Briefly disables CSS transitions during the swap so the colour
 * change is instant — matches `next-themes`'s
 * `disableTransitionOnChange` ergonomics.
 */
function applyResolvedTheme(resolvedTheme: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // Pin transitions off for one tick to avoid the cross-fade.
  const css = document.createElement('style');
  css.appendChild(
    document.createTextNode(
      '*, *::before, *::after { transition: none !important; animation: none !important; }',
    ),
  );
  document.head.appendChild(css);

  if (resolvedTheme === 'dark') {
    root.classList.add(DARK_CLASS);
  } else {
    root.classList.remove(DARK_CLASS);
  }

  // Flush the change synchronously by reading layout, then remove
  // the no-transition style on the next frame.
  void root.offsetHeight;
  window.requestAnimationFrame(() => {
    document.head.removeChild(css);
  });
}

export function ThemeProvider({
  children,
  defaultTheme = 'dark',
}: ThemeProviderProps): JSX.Element {
  // Hydrate from localStorage on first render. SSR-safe — `readStoredTheme`
  // returns null in non-window envs. Falls back to the prop.
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme() ?? defaultTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolve(theme));

  // Apply on every theme change.
  useEffect(() => {
    const resolved = resolve(theme);
    setResolvedTheme(resolved);
    applyResolvedTheme(resolved);
  }, [theme]);

  // Cross-tab sync — if a second tab flips the toggle, this tab follows.
  useEffect(() => {
    function onStorage(e: StorageEvent): void {
      if (e.key !== STORAGE_KEY) return;
      const next = isTheme(e.newValue) ? e.newValue : defaultTheme;
      setThemeState(next);
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [defaultTheme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    persistTheme(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === 'dark' ? 'light' : 'dark';
      persistTheme(next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme, toggleTheme }),
    [theme, resolvedTheme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Read the current theme + setter. Throws if used outside a
 * `<ThemeProvider>` — same shape as `useDemoMode` / `useAuth`.
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
